import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AudienceSpec,
  Document,
  DocumentEvidence,
  DocumentFolder,
  DocumentFolderList,
  DocumentList,
  DocumentVersion,
  DocumentVersionList,
} from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import {
  ActionsCell,
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  Checkbox,
  Drawer,
  Empty,
  Field,
  IconBtn,
  Input,
  Modal,
  Note,
  PageHead,
  Percent,
  Select,
  Table,
  Textarea,
  useAdminToast,
} from '../admin-kit';
import { AudienceField } from '../beheer-communicatie/audience-field';
import { MenuButton } from '../beheer-communicatie/menu-button';
import { LoadError, Loading } from '../beheer-communicatie/page-state';
import { downloadFile } from '../../shared/download';
import { API_BASE, useSession } from '../../shared/session';
import { useUpload } from '../../shared/use-upload';
import { formatDateTime } from '../../shared/format';
import { canPublishVersion } from './version-actions';
import { audienceIncomplete } from '../../shared/audience-preview';

/**
 * Documentbeheer: mappen, documenten en versies (Stap 20, nieuw ontwerp in BHR-5, frame C4).
 *
 * Links de mappen, rechts de documenten van de gekozen map, met per document hoeveel mensen de
 * huidige versie bevestigden. Een nieuwe map, een nieuw document en een nieuwe versie openen elk
 * een venster of paneel; de lijst blijft staan.
 *
 * Het scherm volgt de werkelijke handeling en niet de tabelstructuur. Een preventieadviseur die
 * een nieuwe versie van een werkinstructie heeft, doet drie dingen: bestand kiezen, zeggen wat er
 * veranderd is, publiceren. Dat begint met de knop "Nieuwe versie" op de rij van het document, en
 * niet in een los versiebeheerscherm waar je eerst naartoe moet.
 *
 * Publiceren is een aparte knop na het uploaden, en dat is bewust anders dan bij de agenda: een
 * werkinstructie die bij driehonderd mensen op het scherm komt met het verkeerde bestand erin, is
 * niet terug te draaien — de vorige versie is dan al vervangen.
 */

/** De map die links gekozen is: alles, een echte map, of de documenten zonder map. */
type FolderChoice = 'all' | 'none' | string;

export function DocumentsAdminPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useAdminToast();
  const folders = useFolders();
  const [choice, setChoice] = useState<FolderChoice>('all');
  const [newFolder, setNewFolder] = useState(false);
  const [newDocument, setNewDocument] = useState(false);
  const [versionFor, setVersionFor] = useState<Document | null>(null);
  const [historyFor, setHistoryFor] = useState<Document | null>(null);

  const documents = useQuery({
    queryKey: ['documents'],
    queryFn: async (): Promise<Document[]> => {
      const { data, error } = await api.GET('/api/v1/documents');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as DocumentList).items;
    },
  });

  const all = documents.data ?? [];
  const withoutFolder = all.filter((document) => document.folderId === null).length;
  const shown =
    choice === 'all'
      ? all
      : choice === 'none'
        ? all.filter((document) => document.folderId === null)
        : all.filter((document) => document.folderId === choice);
  const chosenFolder = (folders.data ?? []).find((folder) => folder.id === choice);
  const heading =
    choice === 'all'
      ? t('beheer.communicatie.documents.allDocuments')
      : choice === 'none'
        ? t('documentsAdmin.noFolder')
        : (chosenFolder?.name ?? t('documentsAdmin.documents'));

  return (
    <AdminPage>
      <PageHead
        title={t('documentsAdmin.title')}
        sub={t('beheer.communicatie.documents.sub')}
        actions={
          <>
            <Btn
              variant="sec"
              icon={<AdminIcon name="folder" />}
              testId="folder-new"
              onClick={() => setNewFolder(true)}
            >
              {t('documentsAdmin.newFolder')}
            </Btn>
            <Btn
              icon={<AdminIcon name="plus" />}
              testId="document-new"
              onClick={() => setNewDocument(true)}
            >
              {t('documentsAdmin.newDocument')}
            </Btn>
          </>
        }
      />

      <div className="a-grid c-folders">
        <Card label={t('beheer.communicatie.documents.folders')}>
          <CardPad>
            <nav aria-label={t('beheer.communicatie.documents.folders')}>
              <FolderButton
                label={t('beheer.communicatie.documents.allDocuments')}
                count={documents.isSuccess ? all.length : undefined}
                current={choice === 'all'}
                onClick={() => setChoice('all')}
              />
              {(folders.data ?? []).map((folder) => (
                <FolderButton
                  key={folder.id}
                  label={folder.name}
                  count={folder.documentCount}
                  current={choice === folder.id}
                  onClick={() => setChoice(folder.id)}
                />
              ))}
              {withoutFolder > 0 ? (
                <FolderButton
                  label={t('documentsAdmin.noFolder')}
                  count={withoutFolder}
                  current={choice === 'none'}
                  onClick={() => setChoice('none')}
                />
              ) : null}
            </nav>
          </CardPad>
        </Card>

        <Card>
          <CardHead title={heading} count={documents.isSuccess ? shown.length : undefined} />
          {documents.isPending ? (
            <Loading />
          ) : documents.isError ? (
            <LoadError onRetry={() => void documents.refetch()} error={documents.error} />
          ) : shown.length === 0 ? (
            <Empty
              icon={<AdminIcon name="file" size={24} />}
              title={t('documentsAdmin.emptyTitle')}
              text={t('beheer.communicatie.documents.emptyHint')}
            />
          ) : (
            <Table label={heading} testId="document-table">
              <thead>
                <tr>
                  <th>{t('beheer.communicatie.documents.colDocument')}</th>
                  <th>{t('beheer.communicatie.colAudience')}</th>
                  <th>{t('beheer.communicatie.documents.colConfirmed')}</th>
                  <th className="act">
                    <span className="a-sr">{t('common.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((document) => (
                  <DocumentRow
                    key={document.id}
                    document={document}
                    onNewVersion={() => setVersionFor(document)}
                    onHistory={() => setHistoryFor(document)}
                  />
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>

      <FolderModal
        open={newFolder}
        onClose={() => setNewFolder(false)}
        onDone={() => {
          setNewFolder(false);
          toast.show(t('documentsAdmin.folderCreated'));
        }}
      />

      <DocumentDrawer
        open={newDocument}
        folderId={choice === 'all' || choice === 'none' ? '' : choice}
        onClose={() => setNewDocument(false)}
        onDone={() => {
          setNewDocument(false);
          toast.show(t('documentsAdmin.documentCreated'));
        }}
      />

      {versionFor ? (
        <VersionDrawer
          document={versionFor}
          onClose={() => setVersionFor(null)}
          onDone={(published) => {
            setVersionFor(null);
            toast.show(
              published
                ? t('documentsAdmin.versionPublished')
                : t('documentsAdmin.versionDraftSaved'),
            );
          }}
        />
      ) : null}

      {historyFor ? (
        <HistoryDrawer
          document={historyFor}
          onClose={() => setHistoryFor(null)}
          onMessage={toast.show}
        />
      ) : null}

      {toast.element}
    </AdminPage>
  );
}

function useFolders() {
  const { api } = useSession();
  return useQuery({
    queryKey: ['document-folders'],
    queryFn: async (): Promise<DocumentFolder[]> => {
      const { data, error } = await api.GET('/api/v1/documents/folders');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as DocumentFolderList).items;
    },
  });
}

/** Eén map in de lijst links: icoon, naam, rechts het aantal; de gekozen map blauw. */
function FolderButton({
  label,
  count,
  current,
  onClick,
}: {
  label: string;
  count?: number;
  current: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="a-menu-item c-folder"
      aria-current={current}
      data-testid="document-folder-item"
      onClick={onClick}
    >
      <AdminIcon name="folder" />
      {label}
      {count !== undefined ? <span className="a-menu-right">{count}</span> : null}
    </button>
  );
}

function DocumentRow({
  document,
  onNewVersion,
  onHistory,
}: {
  document: Document;
  onNewVersion: () => void;
  onHistory: () => void;
}) {
  const { t } = useTranslation();
  const current = document.currentVersion;

  return (
    <tr data-testid="document-row">
      <td>
        <div className="a-person">
          <span className="a-tile navy c-tile-sm" aria-hidden="true">
            <AdminIcon name="file" />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="a-name">{document.title}</div>
            <div className="a-meta">
              {current
                ? t('beheer.communicatie.documents.version', { label: current.versionLabel })
                : t('documentsAdmin.noVersion')}
            </div>
          </div>
        </div>
      </td>
      <td>{document.audienceDescription}</td>
      <td>
        <ConfirmedCell version={current} />
      </td>
      <ActionsCell>
        <Btn
          variant="ghost"
          size="sm"
          icon={<AdminIcon name="upload" />}
          testId="document-new-version"
          onClick={onNewVersion}
        >
          {t('documentsAdmin.newVersion')}
        </Btn>
        <MenuButton
          label={document.title}
          trigger={(props) => (
            <IconBtn
              {...props}
              label={t('beheer.communicatie.more')}
              icon={<AdminIcon name="more" />}
              testId="document-more"
            />
          )}
          items={[
            {
              key: 'history',
              label: t('documentsAdmin.showHistory'),
              icon: <AdminIcon name="history" />,
              testId: 'document-history',
              onSelect: onHistory,
            },
          ]}
        />
      </ActionsCell>
    </tr>
  );
}

/**
 * "32 van 51" met een balk (C4).
 *
 * Het totaal staat alleen in het bewijs (`/evidence`), en dat zit achter
 * `documents.evidence.view` omdat het ook de namen bevat. Wie dat recht niet heeft, ziet het aantal
 * zonder totaal - zoals vroeger.
 */
function ConfirmedCell({ version }: { version: DocumentVersion | null }) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const canSeeTotal = has('documents.evidence.view');

  const evidence = useQuery({
    queryKey: ['document-evidence', version?.id],
    enabled: Boolean(version?.requiresAcknowledgement) && canSeeTotal,
    queryFn: async (): Promise<DocumentEvidence> => {
      const { data, error } = await api.GET('/api/v1/documents/versions/{versionId}/evidence', {
        params: { path: { versionId: version?.id ?? '' } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as DocumentEvidence;
    },
  });

  if (!version) return <span className="a-muted">—</span>;
  if (!version.requiresAcknowledgement) {
    return <span className="a-muted">{t('beheer.communicatie.documents.noAcknowledgement')}</span>;
  }
  if (evidence.data) {
    const total = evidence.data.recipientCount;
    const count = version.acknowledgedCount;
    return (
      <Percent
        value={total > 0 ? (count / total) * 100 : 0}
        label={t('beheer.communicatie.documents.confirmedOf', { count, total })}
      />
    );
  }
  return (
    <span className="a-muted">
      {t('documentsAdmin.acknowledgedCount', { count: version.acknowledgedCount })}
    </span>
  );
}

/** Een nieuwe map: één veld, dus een venster en geen paneel. */
function FolderModal({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const { error: failed } = await api.POST('/api/v1/documents/folders', {
        body: { name, parentId: null } as never,
      });
      if (failed) throw failed;
    },
    onSuccess: () => {
      setName('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['document-folders'] });
      onDone();
    },
    onError: (failed) => setError(problemDetail(failed) ?? t('errors.generic')),
  });

  const complete = name.trim().length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('documentsAdmin.newFolder')}
      testId="folder-modal"
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            type="submit"
            form="folder-form"
            disabled={!complete || create.isPending}
            testId="folder-create"
          >
            {t('documentsAdmin.createFolder')}
          </Btn>
        </>
      }
    >
      <form
        id="folder-form"
        className="a-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (complete && !create.isPending) create.mutate();
        }}
      >
        <Field label={t('documentsAdmin.folderName')} error={error}>
          {(props) => (
            <Input
              {...props}
              value={name}
              maxLength={80}
              data-testid="folder-name"
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}

/** Een nieuw document: titel, map en doelgroep. De versies komen daarna. */
function DocumentDrawer({
  open,
  folderId: initialFolder,
  onClose,
  onDone,
}: {
  open: boolean;
  folderId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const folders = useFolders();

  const [title, setTitle] = useState('');
  const [folderId, setFolderId] = useState(initialFolder);
  const [audience, setAudience] = useState<AudienceSpec>({ type: 'all' });
  const [error, setError] = useState<string | null>(null);

  // Wie in een map staat en "Nieuw document" kiest, bedoelt meestal die map.
  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) setFolderId(initialFolder);
  }

  const create = useMutation({
    mutationFn: async () => {
      const { error: failed } = await api.POST('/api/v1/documents', {
        body: { title, folderId: folderId || null, audience } as never,
      });
      if (failed) throw failed;
    },
    onSuccess: () => {
      setTitle('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      void queryClient.invalidateQueries({ queryKey: ['document-folders'] });
      onDone();
    },
    onError: (failed) => setError(problemDetail(failed) ?? t('errors.generic')),
  });

  // Ook geen lege doelgroep: "Kiezen" zonder keuze weigert de server (hertest 30 okt.).
  const complete = title.trim().length > 0 && !audienceIncomplete(audience);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('documentsAdmin.newDocument')}
      testId="document-drawer"
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            type="submit"
            form="document-form"
            disabled={!complete || create.isPending}
            testId="document-create"
          >
            {t('documentsAdmin.createDocument')}
          </Btn>
        </>
      }
    >
      <form
        id="document-form"
        className="a-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (complete && !create.isPending) create.mutate();
        }}
      >
        <Field label={t('documentsAdmin.documentTitle')}>
          {(props) => (
            <Input
              {...props}
              value={title}
              maxLength={200}
              data-testid="document-title"
              onChange={(event) => setTitle(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('documentsAdmin.folder')}>
          {(props) => (
            <Select
              {...props}
              value={folderId}
              data-testid="document-folder"
              onChange={(event) => setFolderId(event.target.value)}
            >
              <option value="">{t('documentsAdmin.noFolder')}</option>
              {(folders.data ?? []).map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {/* Dezelfde doelgroepkiezer als bij een bericht: wie dit één keer leerde, hoeft het niet
            opnieuw te leren. Het verschil zit in wat ermee gebeurt — bij een document wordt de
            doelgroep elke keer opnieuw uitgerekend, bij een bericht één keer bij publicatie. */}
        <AudienceField value={audience} onChange={setAudience} testId="document-audience" />

        {error ? (
          <Note tone="danger" role="alert">
            {error}
          </Note>
        ) : null}
      </form>
    </Drawer>
  );
}

/** Een nieuwe versie: bestand kiezen, zeggen wat er veranderd is, publiceren. */
function VersionDrawer({
  document,
  onClose,
  onDone,
}: {
  document: Document;
  onClose: () => void;
  onDone: (published: boolean) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const upload = useUpload();
  const fileInput = useRef<HTMLInputElement>(null);

  const [versionLabel, setVersionLabel] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [requiresAcknowledgement, setRequiresAcknowledgement] = useState(true);
  const [fileId, setFileId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // De versie die al aangemaakt is. Lukte alleen het publiceren niet, dan publiceert een tweede
  // klik die versie, en maakt hij geen tweede concept met hetzelfde bestand.
  const [createdId, setCreatedId] = useState<string | null>(null);

  const pick = async (file: File) => {
    const uploaded = await upload.upload(file, {
      ownerModule: 'documents',
      purpose: 'document_version',
    });
    setFileId(uploaded);
  };

  const add = useMutation({
    mutationFn: async (meteenPubliceren: boolean) => {
      if (!fileId) throw new Error('geen bestand');
      let versionId = createdId;
      if (!versionId) {
        const created = await api.POST('/api/v1/documents/{documentId}/versions', {
          params: { path: { documentId: document.id } },
          body: { versionLabel, fileId, changeNote, requiresAcknowledgement } as never,
        });
        if (created.error || !created.data) throw created.error ?? new Error('Aanmaken mislukt');
        versionId = (created.data as DocumentVersion).id;
        setCreatedId(versionId);
      }

      /*
        Publiceren is sinds Extra 6 (punt 4) een eigen stap.

        Het scherm publiceerde altijd meteen, omdat er geen plek was waar je een concept terugvond
        - en een concept dat je niet terugvindt, is een verloren bestand. Die plek is er nu: de
        versiegeschiedenis toont concepten met een eigen knop.
      */
      if (!meteenPubliceren) return;

      const published = await api.POST('/api/v1/documents/versions/{versionId}/publish', {
        params: { path: { versionId } },
      });
      if (published.error) throw published.error;
    },
    onSuccess: (_result, meteenPubliceren) => {
      setError(null);
      if (fileInput.current) fileInput.current.value = '';
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      void queryClient.invalidateQueries({ queryKey: ['document-versions', document.id] });
      onDone(meteenPubliceren);
    },
    onError: (failed) => {
      // Ook bij een fout: de versie kan al als concept bestaan en hoort dan in de geschiedenis.
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      void queryClient.invalidateQueries({ queryKey: ['document-versions', document.id] });
      setError(problemDetail(failed) ?? t('errors.generic'));
    },
  });

  const complete = Boolean(fileId) && versionLabel.trim().length > 0;

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('documentsAdmin.newVersion')}
      sub={document.title}
      testId="version-drawer"
      footer={
        <>
          <Btn
            variant="sec"
            disabled={!complete || add.isPending}
            testId="version-draft"
            onClick={() => add.mutate(false)}
          >
            {t('documentsAdmin.saveDraft')}
          </Btn>
          <Btn
            icon={<AdminIcon name="upload" />}
            disabled={!complete || add.isPending}
            testId="version-publish"
            onClick={() => add.mutate(true)}
          >
            {t('documentsAdmin.publishVersion')}
          </Btn>
        </>
      }
    >
      <Field label={t('documentsAdmin.versionLabel')}>
        {(props) => (
          <Input
            {...props}
            value={versionLabel}
            maxLength={40}
            placeholder="v2"
            data-testid="version-label"
            onChange={(event) => setVersionLabel(event.target.value)}
          />
        )}
      </Field>

      <Field label={t('documentsAdmin.changeNote')} optional>
        {(props) => (
          <Textarea
            {...props}
            value={changeNote}
            maxLength={1000}
            data-testid="version-note"
            onChange={(event) => setChangeNote(event.target.value)}
          />
        )}
      </Field>

      <Field
        label={t('documentsAdmin.chooseFile')}
        hint={
          upload.progress !== null
            ? t('media.uploading', { progress: upload.progress })
            : t('beheer.communicatie.documents.fileHint')
        }
        error={upload.error}
      >
        {(props) => (
          <input
            {...props}
            ref={fileInput}
            type="file"
            accept="application/pdf,image/*,video/mp4"
            data-testid="version-file"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void pick(file);
            }}
          />
        )}
      </Field>

      <Checkbox
        checked={requiresAcknowledgement}
        onChange={setRequiresAcknowledgement}
        label={t('documentsAdmin.requiresAcknowledgement')}
        testId="version-requires-ack"
      />

      {error ? (
        <Note tone="danger" role="alert">
          {error}
        </Note>
      ) : null}
    </Drawer>
  );
}

/** De versies van één document, met concepten, herinneren en het bewijs (Extra 6, punt 4). */
function HistoryDrawer({
  document,
  onClose,
  onMessage,
}: {
  document: Document;
  onClose: () => void;
  onMessage: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const [error, setError] = useState<string | null>(null);

  const versions = useQuery({
    queryKey: ['document-versions', document.id],
    queryFn: async (): Promise<DocumentVersion[]> => {
      const { data, error: failed } = await api.GET('/api/v1/documents/{documentId}/versions', {
        params: { path: { documentId: document.id } },
      });
      if (failed || !data) throw failed ?? new Error('Ophalen mislukt');
      return (data as DocumentVersionList).items;
    },
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('documentsAdmin.showHistory')}
      sub={document.title}
      testId="history-drawer"
      footer={<Btn onClick={onClose}>{t('common.close')}</Btn>}
    >
      {error ? (
        <Note tone="danger" role="alert">
          {error}
        </Note>
      ) : null}
      {versions.isPending ? (
        <Loading />
      ) : versions.isError ? (
        <LoadError onRetry={() => void versions.refetch()} error={versions.error} />
      ) : (versions.data ?? []).length === 0 ? (
        <p className="a-hint">{t('documentsAdmin.noVersion')}</p>
      ) : (
        <ul className="c-list">
          {(versions.data ?? []).map((version) => (
            <VersionRow
              key={version.id}
              version={version}
              showEvidence={has('documents.evidence.view')}
              onMessage={(message) => {
                setError(null);
                onMessage(message);
              }}
              onError={setError}
            />
          ))}
        </ul>
      )}
    </Drawer>
  );
}

function VersionRow({
  version,
  showEvidence,
  onMessage,
  onError,
}: {
  version: DocumentVersion;
  showEvidence: boolean;
  onMessage: (message: string) => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api, getAccessToken } = useSession();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  /** Een concept alsnog open zetten (Extra 6, punt 4). */
  const publish = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/documents/versions/{versionId}/publish', {
        params: { path: { versionId: version.id } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      void queryClient.invalidateQueries({ queryKey: ['document-versions'] });
      onMessage(t('documentsAdmin.versionPublished'));
    },
    onError: () => onError(t('errors.generic')),
  });

  /** Herinneren wie nog niet bevestigde (Extra 6, punt 4). */
  const remind = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/api/v1/documents/versions/{versionId}/remind', {
        params: { path: { versionId: version.id } },
      });
      if (error) throw error;
      return (data as { sent: number } | undefined)?.sent ?? 0;
    },
    onSuccess: (sent) =>
      onMessage(
        sent > 0
          ? t('documentsAdmin.reminderSent', { count: sent })
          : t('documentsAdmin.reminderNobody'),
      ),
    onError: () => onError(t('errors.generic')),
  });

  /** Het bewijs als bestand, met wie nog niet bevestigde erbij (Extra 6, punt 4). */
  const exporteren = useMutation({
    mutationFn: () =>
      downloadFile({
        path: `/api/v1/documents/versions/${version.id}/evidence.csv`,
        fileName: `bevestigingen-${version.versionLabel}.csv`,
        token: getAccessToken(),
        baseUrl: API_BASE,
      }),
    onError: () => onError(t('errors.generic')),
  });

  const evidence = useQuery({
    queryKey: ['document-evidence', version.id],
    enabled: open,
    queryFn: async (): Promise<DocumentEvidence> => {
      const { data, error } = await api.GET('/api/v1/documents/versions/{versionId}/evidence', {
        params: { path: { versionId: version.id } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as DocumentEvidence;
    },
  });

  return (
    <li className="a-item" data-testid="version-row">
      <div className="a-item-info">
        <div className="a-item-top">
          <span className="a-name">{version.versionLabel}</span>
          <Badge tone={version.status === 'published' ? 'ok' : 'neutral'}>
            {t(`documentsAdmin.status.${version.status}`)}
          </Badge>
        </div>
        {version.changeNote ? <div className="a-small">{version.changeNote}</div> : null}
        <div className="a-meta">
          {[
            formatDateTime(version.publishedAt ?? version.createdAt),
            t('documentsAdmin.acknowledgedCount', { count: version.acknowledgedCount }),
          ].join(' · ')}
        </div>

        <div className="a-actions">
          {canPublishVersion(version.status) ? (
            <Btn
              size="sm"
              disabled={publish.isPending}
              testId="version-publish-draft"
              onClick={() => publish.mutate()}
            >
              {t('documentsAdmin.publishVersion')}
            </Btn>
          ) : null}
          {version.status === 'published' && version.requiresAcknowledgement ? (
            <Btn
              variant="sec"
              size="sm"
              disabled={remind.isPending}
              testId="version-remind"
              onClick={() => remind.mutate()}
            >
              {t('documentsAdmin.remind')}
            </Btn>
          ) : null}
          {showEvidence ? (
            <>
              <Btn
                variant="sec"
                size="sm"
                icon={<AdminIcon name="download" />}
                disabled={exporteren.isPending}
                testId="version-evidence-export"
                onClick={() => exporteren.mutate()}
              >
                {t('documentsAdmin.exportEvidence')}
              </Btn>
              <Btn
                variant="ghost"
                size="sm"
                aria-expanded={open}
                testId="version-evidence"
                onClick={() => setOpen(!open)}
              >
                {open ? t('documentsAdmin.hideEvidence') : t('documentsAdmin.showEvidence')}
              </Btn>
            </>
          ) : null}
        </div>

        {showEvidence && open && evidence.data ? (
          <ul className="c-list a-small">
            {evidence.data.items.length === 0 ? (
              <li className="a-muted">{t('documentsAdmin.nobodyYet')}</li>
            ) : (
              evidence.data.items.map((row) => (
                <li key={row.personId}>
                  {row.displayName}
                  {row.employeeNumber ? ` (${row.employeeNumber})` : ''} —{' '}
                  {formatDateTime(row.acknowledgedAt)}
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
    </li>
  );
}
